import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Chip,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { db } from "../../firebase-config";
import { doc, getDoc } from "firebase/firestore";

const QuoteDetails = () => {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const quoteRef = doc(db, "quotes", id);
        const quoteSnap = await getDoc(quoteRef);

        if (quoteSnap.exists()) {
          setQuote({ id: quoteSnap.id, ...quoteSnap.data() });
        } else {
          setError("Quote not found.");
        }
      } catch (err) {
        console.error("Error fetching quote:", err);
        setError("Failed to load quote details.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [id]);

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!quote) return null;

  const calculateTotalPrice = () => {
    return quote.items
      .reduce(
        (total, item) => total + (item.price + (item.optionPrice || 0)) * item.quantity,
        0
      )
      .toFixed(2);
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Quote Details
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6">
            Quote ID: {quote.id}{" "}
            <Chip
              label={quote.status.toUpperCase()}
              color={
                quote.status === "pending"
                  ? "warning"
                  : quote.status === "approved"
                  ? "success"
                  : "error"
              }
              sx={{ ml: 1 }}
            />
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Requested on: {new Date(quote.createdAt.toDate()).toLocaleString()}
          </Typography>
          {quote.companyId && (
            <Typography variant="body2">Company ID: {quote.companyId}</Typography>
          )}
        </CardContent>
      </Card>

      <Typography variant="h6" gutterBottom>
        Items
      </Typography>

      <List>
        {quote.items.map((item, index) => (
          <React.Fragment key={index}>
            <ListItem>
              <ListItemText
                primary={`${item.name} ${item.selectedOption ? `(${item.selectedOption})` : ""}`}
                secondary={`Quantity: ${item.quantity} | Base Price: $${item.price.toFixed(2)}${
                  item.optionPrice ? ` | Option Price: $${item.optionPrice.toFixed(2)}` : ""
                } | Total: $${((item.price + (item.optionPrice || 0)) * item.quantity).toFixed(2)}`}
              />
            </ListItem>
            {index < quote.items.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6">Total Price: ${calculateTotalPrice()}</Typography>
      </Box>
    </Container>
  );
};

export default QuoteDetails;
